/**
 * Configuración de Axios con interceptors para manejo automático de tokens
 * Incluye refresh automático y reintentos de peticiones fallidas
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from '@pankod/refine-simple-rest/node_modules/axios';
import { API_URL } from 'constants/environment';
import { StorageUtil } from 'utils/storage.util';
import { authService } from './auth.service';

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
  _skipAuthRefresh?: boolean;
}

/**
 * Crea una instancia de Axios configurada con interceptors
 */
export function createAxiosInstance(): AxiosInstance {
  const instance = axios.create({
    baseURL: API_URL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // ============ REQUEST INTERCEPTOR ============
  instance.interceptors.request.use(
    async (config: AxiosRequestConfig) => {
      const customConfig = config as CustomAxiosRequestConfig;

      // Si la petición debe saltar el refresh automático, continuar
      if (customConfig._skipAuthRefresh) {
        return config;
      }

      // Verificar si el token necesita refresh preventivo (5 min antes de expirar)
      if (StorageUtil.needsRefresh() && !customConfig._retry) {
        try {
          console.log('[Axios] Token expiring soon, refreshing preventively...');
          await authService.refreshToken();
        } catch (error) {
          console.error('[Axios] Preventive refresh failed:', error);
          // Si el refresh preventivo falla, intentar continuar con el token actual
          // El response interceptor manejará el caso de token expirado
        }
      }

      // Agregar access token a la petición
      const token = StorageUtil.getAccessToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // ============ RESPONSE INTERCEPTOR ============
  instance.interceptors.response.use(
    // En caso de respuesta exitosa, simplemente retornarla
    (response) => response,

    // En caso de error, manejar refresh y reintentos
    async (error: AxiosError) => {
      const originalRequest = error.config as CustomAxiosRequestConfig | undefined;

      // Si no hay config, rechazar el error
      if (!originalRequest) {
        return Promise.reject(error);
      }

      // Si la respuesta es 401 (Unauthorized) y no hemos intentado refrescar aún
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          console.log('[Axios] 401 detected, attempting token refresh...');

          // Intentar refresh del token
          await authService.refreshToken();

          // Actualizar el header de autorización con el nuevo token
          const newToken = StorageUtil.getAccessToken();
          if (newToken) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }

          // Reintentar la petición original
          console.log('[Axios] Token refreshed, retrying original request...');
          return instance(originalRequest as AxiosRequestConfig);
        } catch (refreshError) {
          console.error('[Axios] Token refresh failed:', refreshError);

          // Si el refresh falla, limpiar tokens y redirigir al login
          StorageUtil.clearTokens();

          // Redirigir a login
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }

          return Promise.reject(refreshError);
        }
      }

      // Si es otro tipo de error o ya reintentamos, rechazar
      return Promise.reject(error);
    }
  );

  return instance;
}

/**
 * Instancia singleton de Axios configurada
 */
export const axiosInstance = createAxiosInstance();

/**
 * Configuración de Axios para peticiones sin autenticación
 * Útil para login, refresh, etc.
 */
export const axiosPublicInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Helper para marcar una petición como "skip auth refresh"
 * Útil para endpoints de autenticación (login, refresh, logout)
 */
export function skipAuthRefresh(config: AxiosRequestConfig): AxiosRequestConfig {
  return {
    ...config,
    _skipAuthRefresh: true,
  } as AxiosRequestConfig;
}
